import {
  booleanAttribute,
  Component,
  effect,
  forwardRef,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoadingService } from '@shared/services/loading.service';
import { InputComponent } from '@core/components/input/input.component';

type VARIANT = 'outlined' | 'default';
export type COLOR = 'success' | 'error' | 'white' | 'default';
type ICONPOSITION = 'right' | 'left';

export enum DropdownMouseState {
  inside,
  outside,
}

@Component({
  selector: 'pwj-select-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    InputComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectDropdownComponent),
      multi: true,
    },
    {
      provide: NgControl,
      useExisting: forwardRef(() => SelectDropdownComponent),
    },
  ],
  templateUrl: './select-dropdown.component.html',
  styleUrl: './select-dropdown.component.scss',
})
export class SelectDropdownComponent implements OnInit, ControlValueAccessor {
  options = input<{ value: any; label: string }[]>([]);
  placeholder = input<string>('');
  label = input<string>('');
  isDisabled = input<boolean>(false);
  color = input<COLOR>('default');
  variant = input<VARIANT>('outlined');
  icon = input<string>('');
  iconPosition = input<ICONPOSITION>('left');
  messages = input<{ [errorCode: string]: string }>({});
  hasCountryFlag = input(false, { transform: booleanAttribute });
  hasSearch = input(false, { transform: booleanAttribute });
  multiSelect = input(false, { transform: booleanAttribute });
  hideExpandIcon = input(false, { transform: booleanAttribute });

  focus = output<any>();
  blur = output<any>();

  public selectedOptions: { value: any; label: string }[] = [];
  public disabled = false;
  public showMenu: boolean;
  public selectedOption!: string;
  public state: DropdownMouseState;
  public searchTerm: string = '';

  private _control!: NgControl;
  private _writeValueMulti = signal<string[]>([]);

  private readonly _injector = inject(Injector);
  public readonly loadingService = inject(LoadingService);

  @HostListener('document:click') clickedOutside() {
    if (this.state == DropdownMouseState.outside) {
      this.searchTerm = '';
      this.showMenu = false;
    }
  }

  constructor() {
    effect(() => {
      if (this._writeValueMulti()?.length > 0 && this.options()?.length > 0) {
        this.options().forEach(item => {
          if (this._writeValueMulti().includes(item.value)) {
            this.valueChange(item);
          }
        });
      }
    });
    this.showMenu = false;
    this.state = DropdownMouseState.outside;
  }

  get hasErrors(): boolean {
    return !!(
      this._control.errors &&
      (this._control.touched || this._control.dirty) &&
      this._control.invalid &&
      !!this.messages
    );
  }

  get hasRequiredValidator(): boolean {
    const control = this._control.control as FormControl;
    return !!control?.hasValidator(Validators.required);
  }

  get getErrors(): ValidationErrors | null {
    return this._control.errors;
  }

  get filteredOptions(): { value: any; label: string }[] {
    if (!this.showMenu || !this.searchTerm) {
      return this.options();
    }
    return this.options().filter(option =>
      option.label.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  ngOnInit() {
    this._control = this._injector.get(NgControl);
    this.isDisabled() && this.setDisabledState(this.isDisabled());
  }

  propagateChange = (_: any) => {};
  onTouched: () => void = () => {};

  writeValue(obj: string[] | string): void {
    if (this.multiSelect()) {
      if (this._writeValueMulti().length === 0 && obj?.length > 0) {
        this._writeValueMulti.set(obj as string[]);
      }
    } else {
      this.selectedOption = obj as string;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onFocus(): void {
    this.showMenu = true;
    this.focus.emit(true);
  }

  onBlur(): void {
    this.showMenu = true;
    this.blur.emit(true);
    this.onTouched();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  valueChange(item: { value: any; label: string }) {
    if (this.multiSelect()) {
      const index = this.selectedOptions.indexOf(item);
      if (index === -1) {
        this.selectedOptions.push(item);
      } else {
        this.selectedOptions.splice(index, 1);
      }
      this.propagateChange(
        this.selectedOptions.map(selected => selected.value)
      );
    } else {
      if (this.selectedOption !== item.value) {
        this.selectedOption = item.value;
      } else {
        this.selectedOption = '';
      }
      this.propagateChange(item.value);
      this.showMenu = false;
    }
  }

  selectedOptionFn(): string {
    return (
      this.options()?.filter(opt => opt.value === this.selectedOption)[0]
        ?.label || ''
    );
  }
}
